import React, { startTransition, useEffect, useRef, useState } from "react";
import FreeApartments from "./FreeApartments/FreeApartments.jsx";
import Header from "./Header/Header.jsx";
import useAlgolia from "./hooks/useAlgolia.jsx";
import CostUnits from "./CostUnits/CostUnits.jsx";
import AttentionBanner from "./AttentionBanner/AttentionBanner.jsx";
import tampuuriWindowObject from "../tampuuriWindowObject.js";
import useAbortController from "./hooks/useAbortController.jsx";
import useDebouncedHashedParams from "./hooks/useDebouncedHashCache.jsx";
import analyticsApi from "../analyticsApi.js";
import useHelpers from "./hooks/useHelpers.js";

function App(props) {
  const { query } = props;

  const { getData, getFreeApartments } = useAlgolia();

  const [costUnits, setCostUnits] = useState([]);
  const [count, setCount] = useState(0);

  const [state, setState] = useState({});
  const [propertiesInfoText, setPropertiesInfoText] = useState(null);
  const [freeApartmentsInfoText, setFreeApartmentsInfoText] = useState(null);
  const [attentionBanner, setAttentionBanner] = useState(null);
  const { getInfoText, getAttentionBanner, getCachedResults, getSvgs } =
    tampuuriWindowObject();

  const [freeApartments, setFreeApartments] = useState([]);
  const [svgObjects, setSvgObjects] = useState(getSvgs());
  const { generateAnalyticsSearchDone } = analyticsApi();
  const { constructHref } = useHelpers();

  const [params, setParams] = useState({
    locations: query.locations ? query.locations : [],
    rent: query.rent ? query.rent : "",
    rentalTypes: query.rentalTypes ? query.rentalTypes : [],
    extraInfos: query.extraInfos ? query.extraInfos : [],
    priceLevels: query.priceLevels ? query.priceLevels : [],
    surfaceArea: query.surfaceArea ? query.surfaceArea : "",
    page: parseInt(query.page), //Check paginationComponent for explanation
    hitsPerPage: 12,
    exchangeStudents: query.exchangeStudents,
  });

  const { runAbortable } = useAbortController();
  const firstRenderRef = useRef(true);
  const { debouncedParams, hasCache, getCache, setCache } =
    useDebouncedHashedParams(params, 300);

  useEffect(() => {
    let isSuccess = false;

    if (firstRenderRef.current) {
      // First render with pre-fetched cached data
      isSuccess = getCachedServerData();
      firstRenderRef.current = false;
    }

    if (!firstRenderRef.current) {
      // Generate analytics only on debounced params change on non first render
      generateAnalyticsSearchDone({
        locations: debouncedParams.locations,
        rent: debouncedParams.rent,
        extraInfos: [
          ...debouncedParams.rentalTypes,
          ...debouncedParams.extraInfos,
          ...debouncedParams.priceLevels,
        ],
        priceLevels: debouncedParams.priceLevels,
        exchangeStudents: debouncedParams.exchangeStudents ? "kyllä" : "ei",
      });
    }

    if (hasCache()) {
      // Has queried frontend data cached
      getCachedQueryData(debouncedParams);
      return;
    }

    if (!isSuccess && !firstRenderRef.current) {
      // If cache data was not correct --> Fetch data again
      getResults(debouncedParams);
    }
  }, [debouncedParams]);

  /**
   * useDebounceHashCache has already cached these results
   * Fast return the data without network request or any unnecessary state changes
   * @param debouncedParams
   */
  const getCachedQueryData = (debouncedParams) => {
    getCache(debouncedParams).then((cached) => {
      if (
        !cached?.apartments ||
        !cached?.costUnits?.costUnits ||
        !cached?.costUnits?.count
      ) {
        console.error(
          "Cached data failed to fetch, needs to fetch with request",
        );
        getResults(debouncedParams);
        return;
      }

      if (isCostUnitsEmptyDueToPagination(cached?.costUnits)) {
        console.error("Cached data failed due to pagination error");
        callBackUrlQuery({
          ...params,
          page: 1,
        });
        return;
      }

      startTransition(() => {
        setFreeApartments(cached.apartments);
        setCostUnits(cached.costUnits.costUnits);
        setCount(cached.costUnits.count);
      });
    });
  };

  /**
   * Check if the cost unit data is empty due to pagination
   * @param {*} costUnitData
   * @returns
   */
  const isCostUnitsEmptyDueToPagination = (costUnitData) => {
    const { costUnits, count } = costUnitData;

    if (params.page <= 1) {
      // No page parameter or page 1
      return false;
    }

    if (costUnits?.length <= 0 && count > 0) {
      return true;
    }

    return false;
  };

  /**
   * Set states from cached data from server to window object
   * @return bool true if success and false on fail
   */
  const getCachedServerData = () => {
    const cachedAlgoliaData = getCachedResults();

    if (
      !cachedAlgoliaData ||
      !cachedAlgoliaData?.costUnitData?.costUnits ||
      cachedAlgoliaData?.costUnitData?.count ||
      cachedAlgoliaData?.apartments
    ) {
      return false;
    }

    if (isCostUnitsEmptyDueToPagination(cachedAlgoliaData?.costUnitData)) {
      return false;
    }

    setFreeApartments(cachedAlgoliaData.apartments);
    setCostUnits(cachedAlgoliaData.costUnitData.costUnits);
    setCount(cachedAlgoliaData.costUnits.count);
    setPropertiesInfoText(getInfoText("property"));
    setFreeApartmentsInfoText(getInfoText("apartment"));
    setAttentionBanner(getAttentionBanner());
    return true;
  };

  const getResults = (params) => {
    setState({ loading: true });

    let cached = {
      costUnits: null,
      apartments: null,
    };

    runAbortable(getData, params)
      .then((res) => {
        if (isCostUnitsEmptyDueToPagination(res)) {
          // Try to prevent count "flicker" to 0
          setCount(res?.count ?? 0);
          // Re-fetch without page since there are no cost units due to pagination
          callBackUrlQuery({
            ...params,
            page: 1,
          });
          return;
        }

        cached.costUnits = res;
        setCostUnits(res.costUnits);
        setCount(res.count);
        setPropertiesInfoText(getInfoText("property"));
        setFreeApartmentsInfoText(getInfoText("apartment"));
        setAttentionBanner(getAttentionBanner());
        return runAbortable(getFreeApartments, params);
      })
      .then((res) => {
        cached.apartments = res;
        setFreeApartments(res);
        setState({ loading: false });
      })
      .catch((e) => {
        if (e?.name === "AbortError") {
          return;
        }

        setState({ error: e });
      })
      .finally(() => {
        // Set frontend cache
        setCache(params, cached);
      });
  };

  const callBackUrlQuery = (params) => {
    // SetParams sets a debounced state change that triggers only one fetch
    setParams(params);
    const URL = constructHref(params);
    window.history.replaceState(null, null, URL.href); //Changes url but doesnt reload!
  };

  return (
    <div className="tampuuri-properties">
      <main id="content-main">
        <Header
          loading={state?.loading}
          params={params}
          callBackUrlQuery={callBackUrlQuery}
          svgObjects={svgObjects}
        />
        {attentionBanner && (
          <AttentionBanner
            attentionBanner={attentionBanner}
            svgObjects={svgObjects}
          />
        )}

        <section className="element-block--wider">
          <div className="container">
            <div
              id="results-container"
              className="full-width d-flex flex-column"
            >
              {params.page === 1 && freeApartments?.length > 0 && (
                <FreeApartments
                  svgObjects={svgObjects}
                  freeApartments={freeApartments}
                  freeApartmentsInfoText={freeApartmentsInfoText}
                />
              )}
              <CostUnits
                svgObjects={svgObjects}
                costUnits={costUnits}
                count={count}
                params={params}
                callBackUrlQuery={callBackUrlQuery}
                propertiesInfoText={propertiesInfoText}
                state={state}
              />
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default App;
